import { ChangeDetectionStrategy, Component, HostListener, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ImageEditDialogOutput } from '@common/entities/dialog/image-edit-dialog.entity';

import {} from '@mediahaven/shared';
import {
    BaseContainer,
    DialogConfig,
    DialogRef,
    ExportImageFlip,
    LoadingBehaviorSubject,
    MultiFormSelectOptions
} from '@stylehaven/components';
import { CropperPosition, ImageCroppedEvent, ImageTransform, LoadedImage } from 'ngx-image-cropper';
@Component({
    standalone: false,
    selector: 'med-image-edit-dialog',
    templateUrl: './image-edit-dialog.component.html',
    styleUrls: ['./image-edit-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageEditDialogComponent extends BaseContainer implements OnInit {
    public imageLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();
    public imageError$: Observable<any>;

    public readonly PREVIEW_URL: string;
    public readonly ORIGINAL_WIDTH: number;
    public readonly ORIGINAL_HEIGHT: number;
    /**
     * @property y1: Top
     * @property y2: Bottom
     * @property x1: Left
     * @property x2: Right
     */
    private imagePosition: CropperPosition;

    public preserveAspectRatio$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    private scale = 0;
    public computedWidth$: Subject<number> = new Subject<number>();
    public computedHeight$: Subject<number> = new Subject<number>();

    public imageTransform$: BehaviorSubject<ImageTransform> = new BehaviorSubject<ImageTransform>({
        scale: 0,
        rotate: 0,
        flipH: false,
        flipV: false
    });

    private form: UntypedFormGroup;
    public flipOptions: MultiFormSelectOptions<ExportImageFlip>;

    constructor(
        public config: DialogConfig,
        private readonly dialog: DialogRef<ImageEditDialogOutput>,
        private readonly formBuilder: UntypedFormBuilder
    ) {
        super();
        this.imageLoading$.startLoading();

        this.PREVIEW_URL = this.config.data.previewUrl;
        this.ORIGINAL_WIDTH = this.config.data.width;
        this.ORIGINAL_HEIGHT = this.config.data.height;

        this.computedWidth$.next(this.ORIGINAL_WIDTH);
        this.computedHeight$.next(this.ORIGINAL_HEIGHT);
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(event: { target: HTMLInputElement }) {
        this.submit();
    }

    ngOnInit(): void {
        this.verifyPreviewUrl();
        this.initForm();
        this.initFlipOptions();
        this.initFlipOptionSelectedListener();
    }

    private verifyPreviewUrl(): void {
        setTimeout(() => {
            if (!this.PREVIEW_URL) this.loadImageFailed();
        }, 3000);
    }

    private initForm(): void {
        this.form = this.formBuilder.group({
            flip: []
        });

        this.flipFormControl.setValue([ExportImageFlip.NONE]);
    }

    private initFlipOptions(): void {
        this.flipOptions = {
            options: [
                {
                    label: this.translateService.instant('general.filter.none'),
                    value: ExportImageFlip.NONE
                },
                {
                    label: this.translateService.instant('components.image-edit-dialog.flip.horizontal'),
                    value: ExportImageFlip.HORIZONTAL
                },
                {
                    label: this.translateService.instant('components.image-edit-dialog.flip.vertical'),
                    value: ExportImageFlip.VERTICAL
                }
            ],
            isLiveData: false
        };
    }

    private initFlipOptionSelectedListener(): void {
        this.flipFormControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(([flip]: ExportImageFlip) => {
            switch (flip) {
                case ExportImageFlip.HORIZONTAL:
                    this.updateImageTransform('flipH', true);
                    this.updateImageTransform('flipV', false);
                    break;
                case ExportImageFlip.VERTICAL:
                    this.updateImageTransform('flipH', false);
                    this.updateImageTransform('flipV', true);
                    break;
                case ExportImageFlip.NONE:
                default:
                    this.updateImageTransform('flipH', false);
                    this.updateImageTransform('flipV', false);
                    break;
            }
        });
    }

    /**
     * ngx-image-cropper hook
     * Called when the image is cropped. Automatically emits as soon as you let go of the cropper.
     * @param event ImageCroppedEvent
     */
    public imageCropped(event: ImageCroppedEvent): void {
        this.imagePosition = event.imagePosition;
        this.computedWidth$.next(Math.ceil(this.scaleUpPositionValue(event.width)));
        this.computedHeight$.next(Math.ceil(this.scaleUpPositionValue(event.height)));
    }

    /**
     * ngx-image-cropper hook
     * Called when the image first loads in.
     * @param image LoadedImage
     */
    public imageLoaded(image: LoadedImage): void {
        this.scale = this.getScale(image.transformed.size.width, this.ORIGINAL_WIDTH);
        this.imageLoading$.stopLoading();
    }

    private getScale(scaledDown: number, totalValue: number): number {
        return totalValue / scaledDown;
    }

    /**
     * ngx-image-cropper hook
     * Called when the image fails to load.
     */
    public loadImageFailed(): void {
        this.imageError$ = of(new Error('ImageEditDialogComponent:: The image has failed to load.'));
    }

    public submit(): void {
        const output: ImageEditDialogOutput = {
            type: 'success',
            imageOptions: {
                Cropping: {
                    Top: this.imagePosition ? this.scaleUpPositionValue(this.imagePosition.y1) : 0,
                    Bottom: this.imagePosition ? this.determineOffset(this.imagePosition.y2, this.ORIGINAL_HEIGHT) : 0,
                    Left: this.imagePosition ? this.scaleUpPositionValue(this.imagePosition.x1) : 0,
                    Right: this.imagePosition ? this.determineOffset(this.imagePosition?.x2, this.ORIGINAL_WIDTH) : 0
                },
                Flip:
                    this.flipFormControl.value[0] !== ExportImageFlip.NONE ? this.flipFormControl.value[0] : undefined,
                Rotate: this.imageTransform$.value.rotate
            }
        };

        this.dialog.close(output);
    }

    private scaleUpPositionValue(positionValue: number): number {
        return Math.max(0, this.scale * positionValue);
    }

    /**
     * Engine requires we pass the pixel offset from each side of the image.
     * ngx-image-cropper returns CropperPosition with coordinates based on their location starting from the top left. Top & Left are both 0 with the cropper positioned in the top left corner.
     * Bottom and Right take the full value of Height and Width when the cropper is placed in the bottom right corner. To get their offset, we need to subtract the position value from the total value.
     * @param positionValue Bottom or Right
     * @param totalValue Height or Width
     * @returns 0 or H|W minus Bottom|Right
     */
    private determineOffset(positionValue: number, totalValue: number): number {
        return Math.max(0, totalValue - this.scaleUpPositionValue(positionValue));
    }

    public cancel(): void {
        const output: ImageEditDialogOutput = { type: 'cancel' };
        this.dialog.close(output);
    }

    public checkBox(): void {
        this.preserveAspectRatio$.next(!this.preserveAspectRatio$.value);
    }

    public updateImageTransform<T extends keyof ImageTransform>(key: T, value: number | boolean): void {
        this.imageTransform$.next({ ...this.imageTransform$.value, [key]: value });
    }

    get flipFormControl(): UntypedFormControl {
        return this.form.get('flip') as UntypedFormControl;
    }
}
